import React, { useEffect, useState } from 'react';

import { X, CheckCircle2, AlertTriangle, Loader2, ShieldCheck } from 'lucide-react';
import Turnstile, { type TurnstileStatus } from '@/components/Turnstile';
import VerifyContactPanel from '@/components/VerifyContactPanel';
import { requestPostPass, postErrorKey, POST_ERROR_COPY, PENDING_VERIFICATION } from '@/lib/homeownerTrust';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth, crmSync } from '@/contexts/AuthContext';
import { buildSlug, leadPriceFor } from '@/lib/marketplace';
import { submitForm } from '@/lib/crmForms';
import { useOverlayLock } from '@/lib/overlay';
import { markPwaTrigger } from '@/lib/pwa';
import { checkListingZip, zipShapeCheck, zipErrorKey, ZIP_COPY, type ZipCheck } from '@/lib/geoAlerts';
import { statesForZip, zipStateMismatch } from '@/data/zipPrefixStates';



import PhotoUploader from '@/components/PhotoUploader';
import DocumentUploader from '@/components/DocumentUploader';
import {
  DEFAULT_MEDIA,
  ListingDocument,
  MediaSettings,
  loadListingSettings,
  parseDocuments,
} from '@/lib/listingSettings';
import {
  budgetColumnsFor,
  budgetLabels,
  budgetRangeIsValid,
  US_STATES,
  homeownerCategories,
  gcTrades,
  tradeLabel,
} from '@/data/constants';

/**
 * Local bilingual copy for the three strings introduced by the structured
 * budget work. Kept here (one place) instead of duplicated at the call sites.
 */
const FORM_COPY = {
  errBudgetRange: {
    en: 'The minimum budget cannot be higher than the maximum budget.',
    es: 'El presupuesto mínimo no puede ser mayor que el máximo.',
  },
  budgetUnsure: { en: 'Not sure yet', es: 'Aún no estoy seguro' },
  targetDateHint: {
    en: 'Projects with a past start date are reviewed and hidden from the public marketplace unless confirmed still active.',
    es: 'Los proyectos con fecha de inicio pasada se revisan y se ocultan del marketplace público salvo que se confirmen activos.',
  },
} as const;

const inputCls =
  'w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:border-slate-900 focus:outline-none';
const labelCls = 'mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500';


export interface EditableListing {
  id: string;
  title: string;
  description?: string;
  trade: string;
  city: string;
  state: string;
  zip?: string;
  budget_range?: string;
  target_date?: string;
  project_type: string;
  notes?: string;
  photos?: string[] | null;
  documents?: unknown;
}

interface Props {
  mode: 'project' | 'workorder';
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  /** When provided the form edits this listing instead of creating a new one. */
  listing?: EditableListing | null;
}

const ListingForm: React.FC<Props> = ({ mode, open, onClose, onCreated, listing }) => {
  const { t, lang } = useI18n();
  const { profile } = useAuth();
  const [f, setF] = useState<Record<string, string>>({ project_type: mode === 'workorder' ? 'commercial' : 'residential' });
  const [photos, setPhotos] = useState<string[]>([]);
  const [documents, setDocuments] = useState<ListingDocument[]>([]);
  const [media, setMedia] = useState<MediaSettings>(DEFAULT_MEDIA);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [zipStatus, setZipStatus] = useState<ZipCheck | 'idle' | 'checking'>('idle');
  const [zipConfirmed, setZipConfirmed] = useState(false);
  const editing = !!listing;
  // Turnstile (single-use tokens; captchaReset is bumped after each server check).
  const [captcha, setCaptcha] = useState('');
  const [captchaStatus, setCaptchaStatus] = useState<TurnstileStatus>('loading');
  const [captchaReset, setCaptchaReset] = useState(0);
  // True when the server saved the project but held it until the homeowner verifies.
  const [pendingVerification, setPendingVerification] = useState(false);

  // Tell the floating Start Now / WhatsApp stack to step aside while this
  // full-screen form owns the viewport (and lock the page behind it).
  useOverlayLock(open);

  // Admin-configured upload limits (images, PDFs, sizes, MIME types).

  useEffect(() => {
    if (!open) return;
    loadListingSettings().then((s) => setMedia(s.media));
  }, [open]);


  // Load the selected listing into the form (or reset for a new post).
  useEffect(() => {
    if (!open) return;
    setError('');
    setDone(false);
    setPendingVerification(false);
    if (listing) {
      setF({
        title: listing.title || '',
        description: listing.description || '',
        trade: listing.trade || '',
        city: listing.city || '',
        state: listing.state || '',
        zip: listing.zip || '',
        budget_range: listing.budget_range || '',
        target_date: listing.target_date || '',
        project_type: listing.project_type || (mode === 'workorder' ? 'commercial' : 'residential'),
        notes: listing.notes || '',
      });
      setPhotos((listing.photos || []).filter(Boolean));
      setDocuments(parseDocuments(listing.documents));
    } else {
      setF({ project_type: mode === 'workorder' ? 'commercial' : 'residential' });
      setPhotos([]);
      setDocuments([]);
    }
    setZipConfirmed(false);

  }, [open, listing, mode]);

  /**
   * ZIP VALIDATION (2026-10-09). Live, debounced check so the poster sees the
   * problem before pressing Publish. Same rule as the database trigger
   * trg_mkt_listings_validate_zip, which rejects placeholder ZIPs server-side.
   */
  const zipValue = (f.zip || '').trim();
  const originalZip = (listing?.zip || '').trim();
  const zipChanged = !editing || zipValue !== originalZip;
  useEffect(() => {
    if (!open) return;
    setZipConfirmed(false);
    const shape = zipShapeCheck(zipValue);
    if (shape === 'empty') { setZipStatus('idle'); return; }
    if (shape !== 'shape_ok') { setZipStatus(shape); return; }
    setZipStatus('checking');
    let cancelled = false;
    const tmr = window.setTimeout(() => {
      checkListingZip(zipValue).then((r) => { if (!cancelled) setZipStatus(r); });
    }, 300);
    return () => { cancelled = true; window.clearTimeout(tmr); };
  }, [zipValue, open]);

  const set = (k: string, v: string) => setF((p) => ({ ...p, [k]: v }));
  const options = mode === 'workorder' ? gcTrades() : homeownerCategories();
  /**
   * Budget choices in the active language. The value we keep in form state is
   * the LOCALISED label the poster actually picked; budgetColumnsFor() maps it
   * back to the canonical English label plus the structured numbers, so a
   * Spanish post and an English post produce identical database rows.
   */
  const budgets = budgetLabels(lang);

  if (!open) return null;

  const zipMsg = (k: keyof typeof ZIP_COPY) => ZIP_COPY[k][lang === 'es' ? 'es' : 'en'];
  const legacyPlaceholder = editing && !!originalZip && zipShapeCheck(originalZip) !== 'shape_ok';
  const stateMismatch = zipShapeCheck(zipValue) === 'shape_ok' && zipStateMismatch(zipValue, f.state);
  const expectedStates = stateMismatch ? statesForZip(zipValue) : [];

  /**
   * Turn a database / PostgREST error into something a contractor can act on.
   *
   * The posting trigger raises machine codes (documents_must_be_array,
   * too_many_documents, document_path_invalid...). A live customer was shown
   * the raw "documents must be array" string and had no idea what to do, so
   * every known code is mapped to plain bilingual copy here.
   */
  const friendlyError = (msg?: string | null): string => {
    const m = (msg || '').toLowerCase();
    const pk = postErrorKey(m);
    if (pk) return POST_ERROR_COPY[pk][lang === 'es' ? 'es' : 'en'];
    if (m === 'pass_unavailable') return lang === 'es' ? 'No se pudo completar la verificación de seguridad. Inténtelo de nuevo.' : 'The security check could not be completed. Please try again.';
    const zk = zipErrorKey(m);
    if (zk) return zipMsg(zk);
    if (m.includes('documents_must_be_array') || m.includes('documents must be array')) return t('form.errDocsRetry');
    if (m.includes('too_many_documents')) return t('form.errDocsMax');
    if (m.includes('document_too_large')) return t('form.errDocsSize');
    if (m.includes('document_path_invalid') || m.includes('document_not_owned') || m.includes('document_type_invalid'))
      return t('form.errDocsInvalid');
    if (m.includes('too_many_images')) return t('form.errPhotosMax');
    return msg || t('pay.error');
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!f.title || !f.description || !f.trade || !f.city || !f.state) return setError(t('auth.errInvalid'));
    if (!profile) return;

    // ZIP is required for new posts and validated whenever it changes. An
    // untouched legacy ZIP on edit is left alone (same as the DB trigger).
    if (zipChanged) {
      const zc = await checkListingZip(zipValue);
      if (zc === 'empty') return setError(zipMsg('required'));
      if (zc === 'format') return setError(zipMsg('format'));
      if (zc === 'placeholder') return setError(zipMsg('placeholder'));
      if (zipStateMismatch(zipValue, f.state)) return setError(zipMsg('stateMismatch'));
      if (zc === 'unknown' && !zipConfirmed) return setError(zipMsg('unknown'));
    }

    /**
     * STRUCTURED BUDGET (audit follow-up #2)
     * --------------------------------------
     * budget_range stays the human display label; budget_min / budget_max /
     * budget_currency are the numeric columns the marketplace filters and
     * sorts on. They are derived ONCE, here, from the chosen band — never
     * parsed back out of display text later.
     *
     * "Not sure" == no selection: all three columns are written as NULL. No
     * amount is invented, and a listing with no budget on file is never hidden
     * by the minimum-budget filter.
     */
    const budget = budgetColumnsFor(f.budget_range);
    if (!budgetRangeIsValid(budget.budget_min, budget.budget_max)) {
      return setError(FORM_COPY.errBudgetRange[lang] || FORM_COPY.errBudgetRange.en);

    }

    // LAYER 1 — Turnstile on project posting (new posts only). The token is
    // verified server-side and exchanged for a single-use post pass; the
    // database refuses a homeowner insert without one, so this cannot be skipped.
    if (!editing) {
      if (!captcha && captchaStatus !== 'disabled') {
        return setError(POST_ERROR_COPY.captcha[lang === 'es' ? 'es' : 'en']);
      }
      setBusy(true);
      const pass = await requestPostPass(captcha);
      // Tokens are single-use: always fetch a fresh one for the next attempt.
      setCaptchaReset((n) => n + 1);
      if (!pass.ok) {
        setBusy(false);
        return setError(friendlyError(pass.error));
      }
    }

    setBusy(true);

    const price = await leadPriceFor(f.trade, f.project_type || 'residential');

    // `documents` is ALWAYS sent as a JSON array — empty array when the poster
    // attached nothing. parseDocuments() also strips anything that is not a
    // plain {path,name,size,kind,type} object, so a half-finished upload can
    // never turn the column into a string/object and block publishing.
    const docs: ListingDocument[] = parseDocuments(documents);
    const safePhotos: string[] = (photos || []).filter((p) => typeof p === 'string' && p.length > 0);

    // ── EDIT ──────────────────────────────────────────────────────────────
    if (editing && listing) {
      const { error: upErr } = await supabase
        .from('mkt_listings')
        .update({
          title: f.title,
          description: f.description,
          trade: f.trade,
          city: f.city,
          state: f.state,
          zip: zipValue || null,
          ...budget,
          target_date: f.target_date || null,
          project_type: f.project_type || 'residential',
          photos: safePhotos,
          documents: docs,

          notes: f.notes || null,
          lead_price: price,
          /**
           * Editing a project is an explicit statement that it is still live,
           * so the past-target-date expiry clock is reset here. Admin > Expiry
           * queue can also confirm a project without the owner touching it.
           */
          active_confirmed_at: new Date().toISOString(),
          active_confirmed_by: profile.id,
        })
        .eq('id', listing.id)
        .eq('owner_id', profile.id);

      setBusy(false);
      if (upErr) return setError(friendlyError(upErr.message));

      await supabase.from('mkt_activity').insert({
        user_id: profile.id,
        action: mode === 'workorder' ? 'work_order_updated' : 'project_updated',
        detail: `${f.trade} · ${f.city}, ${f.state} · ${safePhotos.length} photos · ${docs.length} docs`,
      });
      setDone(true);
      onCreated();
      return;
    }

    // ── CREATE ────────────────────────────────────────────────────────────
    const slug = buildSlug(f.trade, f.city, f.state);

    const row = {
      owner_id: profile.id,
      source_type: mode === 'workorder' ? 'gc' : 'homeowner',
      slug,
      title: f.title,
      description: f.description,
      trade: f.trade,
      city: f.city,
      state: f.state,
      zip: zipValue || null,
      ...budget,
      target_date: f.target_date || null,
      project_type: f.project_type || 'residential',
      photos: safePhotos,
      documents: docs,

      notes: f.notes || null,
      status: 'published',
      lead_price: price,
      language: lang,
    };

    let { data, error: err } = await supabase.from('mkt_listings').insert(row).select('id,status').maybeSingle();

    // Last-resort safety net: if the attachment payload is ever rejected, the
    // project itself must still publish. Retry once with no attachments and
    // tell the poster their files need re-uploading — never lose the project.
    // (The failed insert rolled back, so the single-use post pass is still unused.)
    if (err && /documents/i.test(err.message || '')) {
      const retry = await supabase
        .from('mkt_listings')
        .insert({ ...row, documents: [] })
        .select('id,status')
        .maybeSingle();
      if (!retry.error && retry.data) {
        data = retry.data;
        err = null;
        setError(t('form.errDocsDropped'));
      }
    }

    if (err || !data) {
      setBusy(false);
      setError(friendlyError(err?.message));
      return;
    }

    const waitingForVerification = data.status === PENDING_VERIFICATION;
    setPendingVerification(waitingForVerification);


    await supabase.from('mkt_listing_contacts').insert({
      listing_id: data.id,
      contact_name:
        profile.company_name || `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.email,
      phone: profile.phone,
      email: profile.email,
      address: f.address || `${f.city}, ${f.state} ${f.zip || ''}`,
    });

    await supabase.from('mkt_activity').insert({
      user_id: profile.id,
      action: mode === 'workorder' ? 'work_order_created' : 'project_created',
      detail: `${f.trade} · ${f.city}, ${f.state}`,
    });

    await crmSync({
      event: mode === 'workorder' ? 'work_order_created' : 'project_created',
      email: profile.email,
      name: profile.company_name || profile.first_name || profile.email,
      phone: profile.phone,
      language: lang,
      userType: profile.user_type,
      details: { Title: f.title, Trade: f.trade, Location: `${f.city}, ${f.state}` },
    });

    // Also push the full submission through the shared form pipeline so the
    // GoHighLevel workflow / pipeline stage for posted work fires.
    await submitForm({
      formType: mode === 'workorder' ? 'work-order-posted' : 'project-posted',
      email: profile.email,
      name: profile.company_name || `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.email,
      phone: profile.phone || undefined,
      language: lang,
      userType: profile.user_type,
      sendEmail: false,
      fields: {
        title: f.title,
        trade: f.trade,
        city: f.city,
        state: f.state,
        zip: f.zip,
        project_type: f.project_type || 'residential',
        budget_range: budget.budget_range || '',
        budget_min: budget.budget_min,
        budget_max: budget.budget_max,
        budget_currency: budget.budget_range ? budget.budget_currency : '',
        target_date: f.target_date,
        lead_price: price,
      },
    });

    setBusy(false);
    setDone(true);
    // Publishing a project is a high-intent moment — offer the installed app
    // so the owner can check responses from their home screen. Still gated by
    // every anti-spam rule in src/lib/pwa.ts.
    markPwaTrigger('project_posted');
    onCreated();
  };



  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center bg-slate-900/60 sm:items-center sm:p-4">
      <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-2xl bg-white p-6 shadow-2xl sm:rounded-2xl">
        <div className="flex items-start justify-between">
          <h2 className="text-xl font-extrabold text-slate-900">
            {editing
              ? mode === 'workorder'
                ? t('form.editWorkOrder')
                : t('form.editProject')
              : mode === 'workorder'
              ? t('dash.postWorkOrder')
              : t('dash.postProject')}
          </h2>
          <button onClick={onClose} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100" aria-label={t('common.close')}>

            <X className="h-5 w-5" />
          </button>
        </div>

        {done && pendingVerification ? (
          <div className="py-6">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <p className="flex items-center gap-2 font-bold text-amber-900">
                <ShieldCheck className="h-5 w-5" />
                {lang === 'es' ? 'Proyecto guardado: falta verificar su cuenta' : 'Project saved — one quick step before it goes live'}
              </p>
              <p className="mt-1.5 text-sm text-amber-900">
                {lang === 'es'
                  ? 'Para proteger a los contratistas de proyectos falsos, cada propietario verifica su correo y su teléfono una sola vez. Su proyecto se publicará automáticamente en cuanto termine.'
                  : 'To protect contractors from fake projects, every homeowner verifies their email and phone once. Your project goes live automatically as soon as you finish.'}
              </p>
            </div>
            <div className="mt-4">
              <VerifyContactPanel />
            </div>
            <button onClick={onClose} className="mt-6 w-full rounded-xl bg-slate-900 px-6 py-3 text-sm font-bold text-white">
              {t('common.close')}
            </button>
          </div>
        ) : done ? (
          <div className="py-10 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
            <p className="mt-4 font-semibold text-slate-800">
              {mode === 'workorder' ? t('form.successWo') : t('form.successProject')}
            </p>
            <button onClick={onClose} className="mt-6 rounded-xl bg-slate-900 px-6 py-3 text-sm font-bold text-white">
              {t('common.close')}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-5 grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className={labelCls} htmlFor="lf-title">{mode === 'workorder' ? t('form.woTitle') : t('form.projectTitle')}</label>
              <input id="lf-title" className={inputCls} value={f.title || ''} onChange={(e) => set('title', e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls} htmlFor="lf-desc">{mode === 'workorder' ? t('form.woDesc') : t('form.projectDesc')}</label>
              <textarea id="lf-desc" rows={4} className={inputCls} value={f.description || ''} onChange={(e) => set('description', e.target.value)} />
            </div>
            <div>
              <label className={labelCls} htmlFor="lf-trade">{mode === 'workorder' ? t('form.tradeRequired') : t('form.category')}</label>
              <select id="lf-trade" className={inputCls} value={f.trade || ''} onChange={(e) => set('trade', e.target.value)}>
                <option value="">—</option>
                {options.map((tr) => (
                  <option key={tr.slug} value={tr.slug}>{tradeLabel(tr.slug, lang)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls} htmlFor="lf-type">{t('form.projectType')}</label>
              <select id="lf-type" className={inputCls} value={f.project_type} onChange={(e) => set('project_type', e.target.value)}>
                <option value="residential">{t('common.residential')}</option>
                <option value="commercial">{t('common.commercial')}</option>
              </select>
            </div>
            {mode === 'project' && (
              <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="lf-address">{t('form.address')}</label>
                <input id="lf-address" className={inputCls} value={f.address || ''} onChange={(e) => set('address', e.target.value)} />
              </div>
            )}
            <div>
              <label className={labelCls} htmlFor="lf-city">{t('common.city')}</label>
              <input id="lf-city" className={inputCls} value={f.city || ''} onChange={(e) => set('city', e.target.value)} />
            </div>
            <div>
              <label className={labelCls} htmlFor="lf-state">{t('common.state')}</label>
              <select id="lf-state" className={inputCls} value={f.state || ''} onChange={(e) => set('state', e.target.value)}>
                <option value="">—</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls} htmlFor="lf-zip">{t('common.zip')} *</label>
              <input
                id="lf-zip"
                inputMode="numeric"
                autoComplete="postal-code"
                maxLength={10}
                placeholder="21044"
                aria-invalid={zipStatus === 'format' || zipStatus === 'placeholder'}
                className={`${inputCls} ${zipStatus === 'format' || zipStatus === 'placeholder' ? 'border-red-400' : ''}`}
                value={f.zip || ''}
                onChange={(e) => set('zip', e.target.value.replace(/[^\d-]/g, '').slice(0, 10))}
              />
              <p className="mt-1.5 flex items-start gap-1.5 text-xs">
                {zipStatus === 'checking' && <><Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin text-slate-400" /><span className="text-slate-500">{lang === 'es' ? 'Verificando…' : 'Checking…'}</span></>}
                {zipStatus === 'ok' && <><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" /><span className="text-emerald-700">{lang === 'es' ? 'Código postal reconocido' : 'ZIP recognised'}</span></>}
                {(zipStatus === 'format' || zipStatus === 'placeholder') && <><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-600" /><span className="text-red-700">{zipMsg(zipStatus)}</span></>}
                {zipStatus === 'unknown' && <><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" /><span className="text-amber-700">{zipMsg('unknown')}</span></>}
                {zipStatus === 'idle' && !legacyPlaceholder && <span className="text-slate-400">{zipMsg('required')}</span>}
              </p>
              {zipStatus === 'unknown' && zipChanged && (
                <label className="mt-2 flex items-start gap-2 text-xs text-slate-700">
                  <input type="checkbox" className="mt-0.5" checked={zipConfirmed} onChange={(e) => setZipConfirmed(e.target.checked)} />
                  <span>{zipMsg('confirm')}</span>
                </label>
              )}
              {legacyPlaceholder && !zipChanged && (
                <p className="mt-2 rounded-lg bg-amber-50 px-2.5 py-2 text-xs text-amber-800">{zipMsg('legacy')}</p>
              )}
              {/* Live ZIP ↔ state check (3-digit prefix, src/data/zipPrefixStates).
                  The database trigger enforces the same rule when the ZIP changes. */}
              {stateMismatch && (
                <p role="alert" className="mt-2 flex items-start gap-1.5 rounded-lg bg-red-50 px-2.5 py-2 text-xs text-red-800">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    {zipMsg('stateMismatch')}{' '}
                    {lang === 'es'
                      ? `(${zipValue.slice(0, 5)} → ${expectedStates.join('/')}, seleccionado: ${(f.state || '').toUpperCase()})`
                      : `(${zipValue.slice(0, 5)} → ${expectedStates.join('/')}, selected: ${(f.state || '').toUpperCase()})`}
                  </span>
                </p>
              )}
            </div>
            <div>
              <label className={labelCls} htmlFor="lf-budget">{mode === 'workorder' ? t('form.budget') : t('form.budgetRange')}</label>
              {/* "Not sure" is the empty value: nothing is stored and no amount
                  is guessed. Every other option writes budget_min/budget_max/
                  budget_currency alongside the display label. */}
              <select id="lf-budget" className={inputCls} value={f.budget_range || ''} onChange={(e) => set('budget_range', e.target.value)}>
                <option value="">{FORM_COPY.budgetUnsure[lang] || FORM_COPY.budgetUnsure.en}</option>
                {budgets.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls} htmlFor="lf-date">{mode === 'workorder' ? t('form.dueDate') : t('form.startDate')}</label>
              <input id="lf-date" type="date" className={inputCls} value={f.target_date || ''} onChange={(e) => set('target_date', e.target.value)} />
              <p className="mt-1.5 text-xs text-slate-400">{FORM_COPY.targetDateHint[lang] || FORM_COPY.targetDateHint.en}</p>

            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>{t('form.photos')}</label>
              <PhotoUploader
                value={photos}
                onChange={setPhotos}
                max={media.max_images}
                maxMb={media.max_image_mb}
                accept={media.image_types}
              />
              <p className="mt-2 text-xs text-slate-400">{t('form.photosHint')}</p>
            </div>

            <div className="sm:col-span-2">
              <label className={labelCls}>{t('form.documents')}</label>
              <DocumentUploader
                value={documents}
                onChange={setDocuments}
                max={media.max_documents}
                maxMb={media.max_document_mb}
                accept={media.document_types}
              />
              <p className="mt-2 text-xs text-slate-400">{t('form.documentsHint')}</p>
            </div>


            <div className="sm:col-span-2">
              <label className={labelCls} htmlFor="lf-notes">{t('form.notes')}</label>
              <textarea id="lf-notes" rows={2} className={inputCls} value={f.notes || ''} onChange={(e) => set('notes', e.target.value)} />
            </div>
            {!editing && (
              <div className="sm:col-span-2">
                <Turnstile
                  action="project_post"
                  onVerify={setCaptcha}
                  onStatus={setCaptchaStatus}
                  resetKey={captchaReset}
                  label={lang === 'es' ? 'Verificación de seguridad contra proyectos falsos' : 'Security check that keeps fake projects off GIGGONE'}
                />
              </div>
            )}
            {error && <p role="alert" className="sm:col-span-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
            <div className="sm:col-span-2">
              <button type="submit" disabled={busy} className="w-full rounded-xl bg-orange-500 px-4 py-3.5 text-base font-bold text-white hover:bg-orange-600 disabled:opacity-50">
                {busy ? t('form.publishing') : editing ? t('form.saveChanges') : t('form.publish')}
              </button>
            </div>

          </form>
        )}
      </div>
    </div>
  );
};

export default ListingForm;
